import { useEffect, useState, useRef, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../../api/client";
import clinicApi, { MEDIA_BASE_URL } from "../../api/clinicClient";
import PortalLayout from "../layout/PortalLayout";
import { AuthContext } from "../../auth/AuthContext";
import { usePortalData } from "../context/PortalDataContext";
import SectionTabs from "./SectionTabs";
import { Dialog }        from "primereact/dialog";
import { Dropdown }      from "primereact/dropdown";
import { InputNumber }   from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast }         from "primereact/toast";

// ── Shared helpers ──────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function RelativeBanner({ name }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: "#eff6ff", border: "1px solid #bfdbfe",
      borderRadius: 10, padding: "10px 16px", marginBottom: 16,
      fontSize: 13, color: "#1e40af",
    }}>
      <i className="pi pi-users" style={{ fontSize: 14, flexShrink: 0 }} />
      <span>Viewing records for <b>{name}</b></span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Uploaded Prescriptions — gf.Prescription, captured at a pharmacy from an
// external/paper prescription (patient upload or pharmacy staff entry).
// ══════════════════════════════════════════════════════════════════════════

function deriveStatus(next_refill) {
  if (!next_refill) return "active";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff  = Math.ceil((new Date(next_refill) - today) / 86400000);
  if (diff < 0)  return "overdue";
  if (diff <= 7) return "due-soon";
  return "active";
}

const UPLOADED_STATUS_CFG = {
  active:    { bg: "#ecfdf5", color: "#059669", label: "Active",   icon: "pi-check-circle"        },
  "due-soon":{ bg: "#fef3c7", color: "#b45309", label: "Due soon", icon: "pi-exclamation-circle"  },
  overdue:   { bg: "#fef2f2", color: "#dc2626", label: "Overdue",  icon: "pi-exclamation-triangle" },
};

function UploadedStatusTag({ status }) {
  const s = UPLOADED_STATUS_CFG[status] ?? UPLOADED_STATUS_CFG.active;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <i className={`pi ${s.icon}`} style={{ fontSize: 10 }} />
      {s.label}
    </span>
  );
}

function NoPharmacy() {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
      <i className="pi pi-building" style={{ fontSize: 32, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--text-secondary)", marginBottom: 6 }}>No pharmacy selected</div>
      <div style={{ fontSize: 13 }}>Please select a pharmacy from the top bar to view your prescriptions.</div>
    </div>
  );
}

function UploadedPrescriptionCard({ rx, onRefill, onViewMedicines }) {
  const status    = deriveStatus(rx.next_refill);
  const firstMed  = rx.items?.[0]?.product ?? "—";
  const moreCount = (rx.items?.length ?? 0) - 1;

  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Row 1: date + status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
          <i className="pi pi-calendar" style={{ fontSize: 10, marginRight: 5 }} />
          {formatDate(rx.date)}
        </span>
        <UploadedStatusTag status={status} />
      </div>

      {/* Row 2: medicines */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>
          <i className="pi pi-tablet" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 600, color: "var(--text-primary)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {firstMed}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {rx.items?.[0]?.dosage
              ? [rx.items[0].dosage, rx.items[0].frequency].filter(Boolean).join(" · ")
              : "Qty set by pharmacist"}
          </div>
        </div>
        {moreCount > 0 && (
          <button
            onClick={() => onViewMedicines(rx.items)}
            style={{
              flexShrink: 0, display: "flex", alignItems: "center", gap: 5,
              padding: "5px 10px", borderRadius: 8, fontSize: 11.5, fontWeight: 500,
              background: "var(--surface-bg)", color: "var(--text-secondary)",
              border: "1px solid var(--border-color)", cursor: "pointer", fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            <i className="pi pi-list" style={{ fontSize: 10 }} />
            +{moreCount} more
          </button>
        )}
      </div>

      {/* Row 3: meta — doctor + next refill */}
      <div style={{
        display: "flex", gap: 16, flexWrap: "wrap",
        padding: "10px 12px", background: "var(--surface-bg)", borderRadius: 8, fontSize: 12,
      }}>
        <div>
          <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>Doctor</div>
          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{rx.doctor || "Not recorded"}</div>
        </div>
        {rx.next_refill && (
          <div>
            <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>Next refill</div>
            <div style={{
              fontWeight: 600,
              color: status === "overdue" ? "#dc2626" : status === "due-soon" ? "#b45309" : "var(--text-primary)",
            }}>
              {formatDate(rx.next_refill)}
            </div>
          </div>
        )}
        {rx.remarks && (
          <div style={{ flex: "1 1 100%" }}>
            <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>Remarks</div>
            <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{rx.remarks}</div>
          </div>
        )}
      </div>

      {/* Row 4: refill button */}
      <button
        onClick={() => onRefill(rx)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          width: "100%", padding: "10px", borderRadius: 10,
          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
          background: "var(--brand-primary)", color: "white",
          border: "none", cursor: "pointer",
        }}
      >
        <i className="pi pi-refresh" style={{ fontSize: 13 }} />
        Request Refill
      </button>
    </div>
  );
}

function MedicineRow({ item, selected, onToggle }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "11px 14px",
        borderBottom: "1px solid var(--border-light)",
        cursor: "pointer",
        background: selected ? "var(--brand-light)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
        border: `2px solid ${selected ? "var(--brand-primary)" : "var(--border-color)"}`,
        background: selected ? "var(--brand-primary)" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {selected && <i className="pi pi-check" style={{ fontSize: 10, color: "white" }} />}
      </div>
      <div style={{
        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
        background: selected ? "rgba(26,111,168,0.12)" : "var(--surface-bg)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: selected ? "var(--brand-primary)" : "var(--text-muted)",
        fontSize: 15, transition: "all 0.15s",
      }}>
        <i className="pi pi-tablet" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: selected ? 600 : 400, fontSize: 13.5, color: "var(--text-primary)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {item.product}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
          {[item.dosage, item.frequency].filter(Boolean).join(" · ") || "Qty set by pharmacist"}
        </div>
      </div>
    </div>
  );
}

function UploadedPrescriptionsPanel() {
  const { orgId }      = useContext(AuthContext);
  const toast          = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const relativeId     = searchParams.get("relative_id");
  const relativeName   = searchParams.get("name");
  const filterRxId    = searchParams.get("filter_rx");
  const relativeParam  = relativeId ? `?relative_id=${relativeId}` : "";

  const [prescriptions,  setPrescriptions]  = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [fetchTick,      setFetchTick]      = useState(0);
  const [historyAccess,  setHistoryAccess]  = useState(null);
  const [linkedSince,    setLinkedSince]    = useState(null);

  useEffect(() => { setFetchTick(t => t + 1); }, []);

  // Dialog state — refill
  const [selectedRx,       setSelectedRx]       = useState(null);
  const [refillVisible,    setRefillVisible]     = useState(false);
  const [selectedItemIds,  setSelectedItemIds]   = useState(new Set());
  const [requestDays,      setRequestDays]       = useState(null);
  const [notes,            setNotes]             = useState("");
  const [submitting,       setSubmitting]        = useState(false);

  // Dialog state — medicine list
  const [medListItems,     setMedListItems]      = useState(null);
  const [medListVisible,   setMedListVisible]    = useState(false);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    api.get(`prescriptions/${relativeParam}`)
      .then(res => {
        const { prescriptions, history_access, linked_since } = res.data;
        setPrescriptions(prescriptions);
        setHistoryAccess(history_access);
        setLinkedSince(linked_since);
      })
      .catch(() => toast.current?.show({
        severity: "error", summary: "Failed to load",
        detail: "Could not fetch prescriptions. Please try again.", life: 4000,
      }))
      .finally(() => setLoading(false));
  }, [orgId, relativeId, fetchTick]);

  // ── Open refill dialog ──────────────────────────────────────────────────────
  const openRefillDialog = (rx) => {
    setSelectedRx(rx);
    setSelectedItemIds(new Set(rx.items.map((it) => it.product_id)));
    const maxDuration = rx.items.reduce((m, it) => Math.max(m, it.duration ?? 0), 0);
    setRequestDays(maxDuration || null);
    setNotes("");
    setRefillVisible(true);
  };

  const toggleItem = (productId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      next.has(productId) ? next.delete(productId) : next.add(productId);
      return next;
    });
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const submitRefill = async () => {
    if (!requestDays) {
      toast.current.show({ severity: "error", summary: "Days required", detail: "Please enter how many days of supply you need.", life: 3000 });
      return;
    }
    if (selectedItemIds.size === 0) {
      toast.current.show({ severity: "warn", summary: "No medicines selected", detail: "Please select at least one medicine to refill.", life: 3000 });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("refill/", {
        prescription_id: selectedRx.id,
        request_days:    requestDays,
        notes:           notes.trim(),
        relative_id:     relativeId || undefined,
        items: selectedRx.items
          .filter((it) => selectedItemIds.has(it.product_id))
          .map((it) => ({ product_id: it.product_id, requested_quantity: null })),
      });
      setRefillVisible(false);
      toast.current.show({ severity: "success", summary: "Request sent", detail: "Your refill request has been sent to the pharmacy.", life: 4000 });
    } catch (err) {
      const detail = err.response?.data?.error ?? err.response?.data?.detail ?? "Something went wrong. Please try again.";
      toast.current.show({ severity: "error", summary: "Request failed", detail, life: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selectedItemIds.size;
  const totalItems    = selectedRx?.items?.length ?? 0;

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {/* ── Medicine list dialog ─────────────────────────────────────────── */}
      <Dialog
        header="All medicines"
        visible={medListVisible}
        onHide={() => setMedListVisible(false)}
        style={{ width: "min(440px, 95vw)" }}
        draggable={false}
        resizable={false}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(medListItems ?? []).map((it, idx) => (
            <div key={idx} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 14px", background: "var(--surface-bg)", borderRadius: 10,
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                background: "var(--brand-light)", color: "var(--brand-primary)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
              }}>
                <i className="pi pi-tablet" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.product}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  {[it.dosage, it.frequency].filter(Boolean).join(" · ") || "Qty set by pharmacist"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Dialog>

      {/* ── Refill dialog ───────────────────────────────────────────────── */}
      <Dialog
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className="pi pi-refresh" style={{ color: "var(--brand-primary)", fontSize: 16 }} />
            Request Refill
          </div>
        }
        visible={refillVisible}
        onHide={() => !submitting && setRefillVisible(false)}
        style={{ width: "min(600px, 95vw)" }}
        breakpoints={{ "768px": "95vw" }}
        closable={!submitting}
        draggable={false}
        resizable={false}
      >
        {selectedRx && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Prescription summary */}
            <div style={{
              background: "var(--brand-light)", borderRadius: 10, padding: "12px 16px",
              display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13,
            }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Prescription date</div>
                <div style={{ fontWeight: 600 }}>{formatDate(selectedRx.date)}</div>
              </div>
              {selectedRx.doctor && (
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Doctor</div>
                  <div style={{ fontWeight: 600 }}>{selectedRx.doctor}</div>
                </div>
              )}
              {selectedRx.next_refill && (
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Next refill</div>
                  <div style={{ fontWeight: 600 }}>{formatDate(selectedRx.next_refill)}</div>
                </div>
              )}
            </div>

            {/* How it works */}
            <div style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              borderRadius: 10, padding: "10px 14px",
            }}>
              <i className="pi pi-info-circle" style={{ color: "#16a34a", fontSize: 14, marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontSize: 12.5, color: "#166534", lineHeight: 1.6 }}>
                <b>How it works:</b> Select the medicines you need, tell us how many days of supply, and the pharmacist will prepare the correct quantities for you.
              </div>
            </div>

            {/* Medicine selection */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.4px", textTransform: "uppercase" }}>
                  Select medicines
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {selectedCount} of {totalItems} selected
                </div>
              </div>
              <div style={{ border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {selectedRx.items.map((item, idx) => (
                  <div key={item.product_id} style={{ borderBottom: idx === selectedRx.items.length - 1 ? "none" : undefined }}>
                    <MedicineRow
                      item={item}
                      selected={selectedItemIds.has(item.product_id)}
                      onToggle={() => toggleItem(item.product_id)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Days of supply */}
            <div>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 8 }}>
                How many days of supply do you need?
                <span style={{ color: "#dc2626", marginLeft: 3 }}>*</span>
              </label>
              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                {[7, 14, 30, 60, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setRequestDays(d)}
                    style={{
                      padding: "6px 14px", borderRadius: 20, fontSize: 12.5,
                      border: `1.5px solid ${requestDays === d ? "var(--brand-primary)" : "var(--border-color)"}`,
                      background: requestDays === d ? "var(--brand-primary)" : "transparent",
                      color: requestDays === d ? "white" : "var(--text-secondary)",
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                    }}
                  >
                    {d} days
                  </button>
                ))}
              </div>
              <InputNumber
                value={requestDays}
                onValueChange={(e) => setRequestDays(e.value)}
                placeholder="Or type a custom number…"
                min={1} max={365} suffix=" days"
                inputStyle={{ width: "100%", fontSize: 13 }}
              />
            </div>

            {/* Notes */}
            <div>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 6 }}>
                Notes to pharmacist
                <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 4 }}>(optional)</span>
              </label>
              <InputTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                style={{ width: "100%", fontSize: 13 }}
                placeholder="e.g. prefer brand name, have allergy to generic coating…"
                autoResize
              />
            </div>

            {/* Footer */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              paddingTop: 6, borderTop: "1px solid var(--border-light)",
            }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {selectedCount > 0 && requestDays
                  ? `${selectedCount} medicine${selectedCount !== 1 ? "s" : ""} · ${requestDays} days`
                  : "Select medicines and days above"}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setRefillVisible(false)}
                  disabled={submitting}
                  style={{
                    padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
                    background: "none", border: "1px solid var(--border-color)",
                    color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit",
                    opacity: submitting ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRefill}
                  disabled={submitting || selectedCount === 0 || !requestDays}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: "var(--brand-primary)", color: "white", border: "none",
                    cursor: (submitting || selectedCount === 0 || !requestDays) ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    opacity: (submitting || selectedCount === 0 || !requestDays) ? 0.6 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  <i className={`pi ${submitting ? "pi-spin pi-spinner" : "pi-send"}`} style={{ fontSize: 12 }} />
                  {submitting ? "Sending…" : "Send Request"}
                </button>
              </div>
            </div>

          </div>
        )}
      </Dialog>

      {relativeId && relativeName && <RelativeBanner name={relativeName} />}

      {historyAccess === "future_only" && linkedSince && (
        <div style={{
          background: "#fffbeb", border: "1px solid #f59e0b",
          borderRadius: 10, padding: "12px 16px",
          fontSize: 13, color: "#92400e",
          display: "flex", alignItems: "flex-start", gap: 10,
          marginBottom: 16,
        }}>
          <i className="pi pi-info-circle" style={{ color: "#f59e0b", fontSize: 16, marginTop: 1, flexShrink: 0 }} />
          <span>
            Showing records from <strong>{linkedSince}</strong> onwards.
            Visit your pharmacy in person to request access to earlier records.
          </span>
        </div>
      )}

      {!orgId ? (
        <div className="ph-card"><NoPharmacy /></div>
      ) : loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading prescriptions…</div>
        </div>
      ) : prescriptions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-inbox" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No uploaded prescriptions found</div>
          <div style={{ fontSize: 13 }}>Prescriptions you upload or that your pharmacy records will appear here.</div>
        </div>
      ) : (
        <>
          {filterRxId && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "var(--brand-light)", border: "1px solid var(--brand-accent)",
              borderRadius: 10, padding: "10px 14px", marginBottom: 12,
              fontSize: 13,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--brand-primary)" }}>
                <i className="pi pi-filter" style={{ fontSize: 12 }} />
                <span style={{ fontWeight: 500 }}>Showing linked prescription</span>
              </div>
              <button
                onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete("filter_rx");
                  navigate(`?${p.toString()}`, { replace: true });
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: "none", border: "1px solid var(--brand-accent)",
                  borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 500,
                  color: "var(--brand-primary)", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <i className="pi pi-times" style={{ fontSize: 10 }} /> Show all
              </button>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(filterRxId ? prescriptions.filter(rx => rx.id === filterRxId) : prescriptions).map((rx) => (
            <UploadedPrescriptionCard
              key={rx.id}
              rx={rx}
              onRefill={openRefillDialog}
              onViewMedicines={(items) => { setMedListItems(items); setMedListVisible(true); }}
            />
          ))}
        </div>
        </>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// E-Prescriptions — clinic.ClinicPrescription, issued live in-app by a
// doctor during/after a Goldfish clinic consultation.
// ══════════════════════════════════════════════════════════════════════════

const CLINIC_STATUS_CFG = {
  issued:    { bg: "#f3f4f6", color: "#374151", label: "Issued" },
  sent:      { bg: "#eff6ff", color: "#2563eb", label: "Sent to Pharmacy" },
  accepted:  { bg: "#fef3c7", color: "#b45309", label: "Accepted" },
  dispensed: { bg: "#ecfdf5", color: "#059669", label: "Dispensed" },
  expired:   { bg: "#fef2f2", color: "#dc2626", label: "Expired" },
  cancelled: { bg: "#fef2f2", color: "#dc2626", label: "Cancelled" },
};

function ClinicStatusTag({ status }) {
  const s = CLINIC_STATUS_CFG[status] ?? { bg: "#f3f4f6", color: "#6b7280", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

function AssignPharmacyDialog({ prescription, pharmacies, onClose, onAssigned }) {
  const [pharmacyId, setPharmacyId] = useState(null);
  // Prefilled from whatever the doctor suggested (if anything) — always
  // the patient's own call to change, see ClinicPrescription.
  // dispensing_duration_days's docstring on the backend.
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    if (prescription) setDays(prescription.dispensing_duration_days ?? null);
  }, [prescription]);

  if (!prescription) return null;

  const handleAssign = () => {
    if (!pharmacyId) return;
    setSaving(true);
    clinicApi.post(`prescriptions/${prescription.id}/assign-pharmacy/`, {
      pharmacy_org_id: pharmacyId,
      dispensing_duration_days: days || undefined,
    })
      .then(() => {
        onAssigned();
        onClose();
      })
      .catch(err => {
        toast.current?.show({
          severity: "error", summary: "Could not assign",
          detail: err.response?.data?.error || "Please try again.", life: 4000,
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog header="Send to Pharmacy" visible onHide={onClose} style={{ width: "min(420px, 95vw)" }} draggable={false} resizable={false}>
      <Toast ref={toast} />
      <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 0 }}>
        Choose one of your linked pharmacies to fulfill this prescription.
      </p>
      {pharmacies.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          You have no linked pharmacies yet. Link one from the Home page first.
        </div>
      ) : (
        <>
          <Dropdown
            value={pharmacyId}
            options={pharmacies}
            optionLabel="name"
            optionValue="organization_id"
            onChange={(e) => setPharmacyId(e.value)}
            placeholder="Select a pharmacy"
            style={{ width: "100%", marginBottom: 16 }}
          />
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 6 }}>
            How many days?
            <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 4 }}>
              (optional{prescription.prescribed_duration_days ? ` — course is ${prescription.prescribed_duration_days} days` : ""})
            </span>
          </label>
          <InputNumber
            value={days}
            onValueChange={(e) => setDays(e.value)}
            placeholder="e.g. 30"
            min={1} max={365} suffix=" days"
            inputStyle={{ width: "100%", fontSize: 13 }}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <button
            onClick={handleAssign}
            disabled={!pharmacyId || saving}
            style={{
              width: "100%", padding: "10px", borderRadius: 10, fontSize: 13.5, fontWeight: 700, fontFamily: "inherit",
              background: "var(--brand-primary)", color: "white", border: "none",
              cursor: !pharmacyId || saving ? "not-allowed" : "pointer", opacity: !pharmacyId || saving ? 0.6 : 1,
            }}
          >
            {saving ? "Sending…" : "Send Prescription"}
          </button>
        </>
      )}
    </Dialog>
  );
}

function ClinicMedicineList({ items, visible, onHide }) {
  return (
    <Dialog
      header="All medicines"
      visible={visible}
      onHide={onHide}
      style={{ width: "min(440px, 95vw)" }}
      draggable={false}
      resizable={false}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(items ?? []).map((it, idx) => (
          <div key={idx} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 14px", background: "var(--surface-bg)", borderRadius: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: "var(--brand-light)", color: "var(--brand-primary)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
            }}>
              <i className="pi pi-tablet" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>{it.product_name}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                {[it.dosage, it.frequency, it.duration_days ? `${it.duration_days} days` : null].filter(Boolean).join(" · ") || "—"}
                {it.quantity ? ` · Qty ${it.quantity}` : ""}
              </div>
              {it.instructions && (
                <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2, fontStyle: "italic" }}>{it.instructions}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

function EPrescriptionCard({ rx, onAssign, onViewMedicines }) {
  const firstItem = rx.items?.[0];
  const moreCount = (rx.items?.length ?? 0) - 1;
  const canAssign = rx.routing_method === "via_patient" && rx.status === "issued";

  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
          <i className="pi pi-calendar" style={{ fontSize: 10, marginRight: 5 }} /> {formatDate(rx.date)}
        </span>
        <ClinicStatusTag status={rx.status} />
      </div>

      {/* Doctor + clinic — always shown, reinforces this is a real doctor's prescription */}
      <div style={{
        display: "flex", gap: 16, flexWrap: "wrap",
        padding: "10px 12px", background: "var(--surface-bg)", borderRadius: 8, fontSize: 12,
      }}>
        <div>
          <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>Doctor</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, color: "var(--text-primary)" }}>
            {rx.doctor_photo_url && (
              <img
                src={`${MEDIA_BASE_URL}${rx.doctor_photo_url}`}
                alt=""
                style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              />
            )}
            Dr. {rx.doctor_name}
          </div>
        </div>
        <div>
          <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>Clinic</div>
          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{rx.clinic_name}</div>
        </div>
      </div>

      {/* Medicines */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>
          <i className="pi pi-tablet" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 600, color: "var(--text-primary)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {firstItem?.product_name ?? "—"}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {firstItem
              ? [firstItem.dosage, firstItem.frequency].filter(Boolean).join(" · ") || "—"
              : "No items"}
          </div>
        </div>
        {moreCount > 0 && (
          <button
            onClick={() => onViewMedicines(rx.items)}
            style={{
              flexShrink: 0, display: "flex", alignItems: "center", gap: 5,
              padding: "5px 10px", borderRadius: 8, fontSize: 11.5, fontWeight: 500,
              background: "var(--surface-bg)", color: "var(--text-secondary)",
              border: "1px solid var(--border-color)", cursor: "pointer", fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            <i className="pi pi-list" style={{ fontSize: 10 }} />
            +{moreCount} more
          </button>
        )}
      </div>

      {(rx.prescribed_duration_days || rx.dispensing_duration_days || rx.follow_up_days) && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "var(--text-muted)" }}>
          {rx.prescribed_duration_days && (
            <span><i className="pi pi-clock" style={{ fontSize: 10, marginRight: 5 }} /> Course: {rx.prescribed_duration_days} days</span>
          )}
          {rx.dispensing_duration_days && (
            <span><i className="pi pi-box" style={{ fontSize: 10, marginRight: 5 }} /> Suggested: {rx.dispensing_duration_days} days</span>
          )}
          {rx.follow_up_days && (
            <span><i className="pi pi-refresh" style={{ fontSize: 10, marginRight: 5 }} /> Follow-up in {rx.follow_up_days} days</span>
          )}
        </div>
      )}

      {rx.target_pharmacy_name && (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          <i className="pi pi-shop" style={{ fontSize: 10, marginRight: 5 }} /> Routed to {rx.target_pharmacy_name}
        </div>
      )}

      {rx.notes && (
        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          <i className="pi pi-comment" style={{ fontSize: 10, marginRight: 5 }} /> {rx.notes}
        </div>
      )}

      {canAssign && (
        <button
          onClick={() => onAssign(rx)}
          style={{
            alignSelf: "flex-start", padding: "7px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
            background: "var(--brand-primary)", color: "white", border: "none", cursor: "pointer",
          }}
        >
          <i className="pi pi-send" style={{ fontSize: 11, marginRight: 6 }} />
          Send to Pharmacy
        </button>
      )}
    </div>
  );
}

function EPrescriptionsPanel() {
  const { pharmacies } = usePortalData();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [medListItems, setMedListItems] = useState(null);
  const [medListVisible, setMedListVisible] = useState(false);

  const load = () => {
    setLoading(true);
    clinicApi.get("prescriptions/")
      .then(res => setPrescriptions(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <>
      <AssignPharmacyDialog
        prescription={assigning}
        pharmacies={pharmacies}
        onClose={() => setAssigning(null)}
        onAssigned={load}
      />
      <ClinicMedicineList items={medListItems} visible={medListVisible} onHide={() => setMedListVisible(false)} />

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading prescriptions…</div>
        </div>
      ) : prescriptions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-file-edit" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No e-prescriptions yet</div>
          <div style={{ fontSize: 13 }}>Prescriptions issued during a clinic visit will appear here.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {prescriptions.map(rx => (
            <EPrescriptionCard
              key={rx.id}
              rx={rx}
              onAssign={setAssigning}
              onViewMedicines={(items) => { setMedListItems(items); setMedListVisible(true); }}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Merged page
// ══════════════════════════════════════════════════════════════════════════

export default function Prescriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterRxId = searchParams.get("filter_rx");
  const requestedTab = searchParams.get("tab");
  // A deep link into an uploaded prescription (from an invoice's "origin" link,
  // or a refill notification) should land on that tab even without ?tab=.
  const [tab, setTab] = useState(requestedTab === "uploaded" || filterRxId ? "uploaded" : "e");

  const setTabAndParam = (key) => {
    setTab(key);
    const p = new URLSearchParams(searchParams);
    p.set("tab", key);
    setSearchParams(p, { replace: true });
  };

  return (
    <PortalLayout>
      <div className="page-header">
        <h1 className="page-title">My Prescriptions</h1>
        <p className="page-subtitle">
          {tab === "e"
            ? "Issued live by a doctor during a clinic visit"
            : "Uploaded or recorded at a pharmacy from an outside prescription"}
        </p>
      </div>

      <SectionTabs
        tabs={[
          { key: "e",        label: "E-Prescriptions",       icon: "pi-file-edit" },
          { key: "uploaded", label: "Uploaded Prescriptions", icon: "pi-upload"    },
        ]}
        active={tab}
        onChange={setTabAndParam}
      />

      {tab === "e" ? <EPrescriptionsPanel /> : <UploadedPrescriptionsPanel />}
    </PortalLayout>
  );
}
